import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { Work, LocationOn, Business, AttachMoney, Event } from "@mui/icons-material";
import Navbar from "../../Navbar/Navbar";
import baseURL from "../../config";

const ListofVacancy = () => {
  const [vacancies, setVacancies] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedVessel, setSelectedVessel] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch vacancies
  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseURL}/api/vacancy`);
        if (res.data.success) {
          setVacancies(res.data.data);
        } else {
          setError("Failed to fetch vacancies");
        }
      } catch (err) {
        console.error("Error fetching vacancies:", err);
        setError("Unable to load vacancies. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchVacancies();
  }, []);

  // Extract unique vessel types
  const vesselTypes = ["All", ...new Set(vacancies.map((v) => v.vesselType))];

  // Filter + Sort logic
  const filteredVacancies = vacancies
    .filter((vacancy) => {
      const matchesSearch = [
        vacancy.title,
        vacancy.rank,
        vacancy.location,
        vacancy.vesselType,
        vacancy.postedBy?.name,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesVessel =
        selectedVessel === "All" || vacancy.vesselType === selectedVessel;

      return matchesSearch && matchesVessel;
    })
    .sort((a, b) => {
      if (sortBy === "salaryHigh") {
        return (b.salary?.max || 0) - (a.salary?.max || 0);
      }
      if (sortBy === "salaryLow") {
        return (a.salary?.min || 0) - (b.salary?.min || 0);
      }
      if (sortBy === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0;
    });

  // Handle vacancy activation
  const handleActivate = async (id) => {
    try {
      await axios.put(`${baseURL}/api/vacancy/activate/${id}`);
      setVacancies((prev) =>
        prev.map((v) => (v._id === id ? { ...v, isActive: true } : v))
      );
    } catch (err) {
      console.error("Error activating vacancy:", err);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
        <Grid container spacing={3}>
          {/* Sidebar Filters */}
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                boxShadow: 3,
                position: "sticky",
                top: 90,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Filter by Vessel Type
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {vesselTypes.map((type) => (
                  <ListItem
                    key={type}
                    button
                    selected={selectedVessel === type}
                    onClick={() => setSelectedVessel(type)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    }}
                  >
                    <ListItemText primary={type} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>

          {/* Job List */}
          <Grid item xs={12} md={9}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="red">
                🚢 Job Vacancies
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  variant="outlined"
                  placeholder="Search jobs..."
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    width: 250,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="salaryHigh">Salary: High → Low</MenuItem>
                    <MenuItem value="salaryLow">Salary: Low → High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Loading state */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Error state */}
            {!loading && error && (
              <Box sx={{ textAlign: "center", mt: 8, color: "red" }}>
                <Typography variant="h6">{error}</Typography>
              </Box>
            )}

            {/* No vacancies */}
            {!loading && !error && filteredVacancies.length === 0 && (
              <Box sx={{ textAlign: "center", mt: 8, color: "gray" }}>
                <Typography variant="h6">No vacancies found</Typography>
                <Typography variant="body2">
                  Try adjusting filters or search terms.
                </Typography>
              </Box>
            )}

            {/* Vacancy Cards */}
            {!loading &&
              !error &&
              filteredVacancies.map((vacancy) => (
                <Card
                  key={vacancy._id}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    transition: "0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  {/* Gradient header bar */}
                  <Box
                    sx={{
                      height: 4,
                      background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                    }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        src={vacancy.img?.filePath}
                        alt={vacancy.title}
                        sx={{ width: 45, height: 45 }}
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {vacancy.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Business fontSize="small" sx={{ mr: 0.5 }} />
                          {vacancy.postedBy?.name || "Unknown"}
                        </Typography>
                      </Box>
                      <Chip
                        label={vacancy.vesselType}
                        color="primary"
                        size="small"
                        sx={{ ml: "auto" }}
                      />
                    </Box>

                    <Grid container spacing={1.5} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <Work fontSize="small" sx={{ mr: 0.5 }} />
                          {vacancy.rank}
                        </Typography>
                        <Typography variant="body2">
                          <AttachMoney fontSize="small" sx={{ mr: 0.5 }} />
                          {vacancy.salary?.min} - {vacancy.salary?.max}{" "}
                          {vacancy.salary?.currency}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <Event fontSize="small" sx={{ mr: 0.5 }} />
                          {vacancy.joiningDate
                            ? new Date(
                                vacancy.joiningDate
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "Not specified"}
                        </Typography>
                        <Typography variant="body2">
                          <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                          {vacancy.location}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Requirements */}
                    <Box sx={{ mt: 1 }}>
                      {vacancy.requirements?.slice(0, 2).map((req, i) => (
                        <Chip
                          key={i}
                          label={req}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>

                    {/* Status Button */}
                    {vacancy.isActive ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        disabled
                        sx={{
                          mt: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: "bold",
                          py: 0.6,
                          fontSize: "0.8rem",
                        }}
                      >
                        Active
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        sx={{
                          mt: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: "bold",
                          py: 0.6,
                          fontSize: "0.8rem",
                        }}
                        onClick={() => handleActivate(vacancy._id)}
                      >
                        Activate
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ListofVacancy;
